import { ethers } from 'ethers';
import { WalletProvider, WALLET_TYPES } from './WalletProvider.js';

/**
 * Private Key wallet provider implementation
 * 
 * Allows users to connect using their private key directly. This provides
 * more reliable event handling since it doesn't depend on browser extensions
 * or injected providers that can have connectivity issues.
 * 
 * Security: Private keys are stored only in memory during the session.
 * Users are responsible for their private key security.
 */
export class PrivateKeyProvider extends WalletProvider {
  constructor() {
    super();
    this.provider = null;
    this.signer = null;
    this.wallet = null;
    this.privateKey = null;
    this.connectionError = '';
    this.sessionTimeout = null;
    
    // Custom RPC wallet - users provide their own RPC endpoints
    this.sessionTimeoutMinutes = 30; // Auto-logout after 30 minutes (default)
    this.isPersistent = false; // Whether to save keys to browser storage
    this.indefinitePersistence = false; // Whether to preserve keys indefinitely
  }

  getType() {
    return WALLET_TYPES.CUSTOM_RPC;
  }

  getDisplayName() {
    return 'Custom RPC Wallet';
  }

  isAvailable() {
    // Private key provider is always available
    return true;
  }

  async connect(privateKey, rpcUrl, persistenceMode = 'session') {
    if (!privateKey) {
      throw new Error('Private key is required for connection');
    }

    if (!rpcUrl) {
      throw new Error('RPC URL is required for connection');
    }

    // Validate private key format
    if (!this.isValidPrivateKey(privateKey)) {
      this.connectionError = 'Invalid private key format. Please check your private key.';
      throw new Error(this.connectionError);
    }

    // Validate RPC URL format
    if (!this.isValidRpcUrl(rpcUrl)) {
      this.connectionError = 'Invalid RPC URL format. Please provide a valid HTTP/HTTPS URL.';
      throw new Error(this.connectionError);
    }

    // Initialize with user-provided RPC URL
    await this.initializeWithRpcUrl(rpcUrl);

    try {
      // Create wallet from private key
      this.wallet = new ethers.Wallet(privateKey, this.provider);
      this.signer = this.wallet;
      this.privateKey = privateKey;
      this.connectionError = '';
      
      // Handle persistence modes
      switch (persistenceMode) {
        case 'session':
          this.isPersistent = false;
          this.indefinitePersistence = false;
          this.sessionTimeoutMinutes = 30; // 30 minutes
          break;
        case 'timed':
          this.isPersistent = true;
          this.indefinitePersistence = false;
          this.sessionTimeoutMinutes = 480; // 8 hours
          this.saveToBrowser(privateKey, rpcUrl, 'timed');
          console.log('🔑 Private key and RPC URL saved to browser storage (8 hours)');
          break;
        case 'indefinite':
          this.isPersistent = true;
          this.indefinitePersistence = true;
          this.sessionTimeoutMinutes = null; // No timeout
          this.saveToBrowser(privateKey, rpcUrl, 'indefinite');
          console.log('🔑 Private key and RPC URL saved to browser storage (indefinite)');
          break;
        default:
          throw new Error(`Invalid persistence mode: ${persistenceMode}`);
      }
      
      // Verify wallet can sign (test the connection)
      const address = await this.wallet.getAddress();
      console.log('🔑 Connected with private key wallet:', address);
      
      // Set up session timeout (only if not indefinite)
      if (this.sessionTimeoutMinutes !== null) {
        this.setupSessionTimeout();
      }
      
      // Store connection time for session management
      this.connectionTime = Date.now();
      
      return true;
    } catch (error) {
      this.connectionError = `Failed to create wallet from private key: ${error.message}`;
      throw new Error(this.connectionError);
    }
  }

  async disconnect(forceClear = false) {
    // Clear sensitive data
    this.signer = null;
    this.wallet = null;
    this.privateKey = null;
    this.connectionError = '';
    
    // Clear browser storage only if:
    // 1. forceClear is true (manual deletion), OR  
    // 2. it was timed persistence (not indefinite)
    if (forceClear || (this.isPersistent && !this.indefinitePersistence)) {
      this.clearFromBrowser();
      console.log('🔑 Cleared saved private key from browser storage');
    }
    
    // Reset persistence flags (but don't clear indefinite keys unless forced)
    if (forceClear || !this.indefinitePersistence) {
      this.isPersistent = false;
      this.indefinitePersistence = false;
    }
    
    // Clear session timeout
    if (this.sessionTimeout) {
      clearTimeout(this.sessionTimeout);
      this.sessionTimeout = null;
    }
    
    console.log('🔑 Private key provider disconnected');
  }

  getProvider() {
    return this.provider;
  }

  getSigner() {
    return this.signer;
  }

  async getAccounts() {
    if (!this.wallet) {
      return [];
    }

    try {
      const address = await this.wallet.getAddress();
      return [address];
    } catch (error) {
      console.error('Error getting accounts:', error);
      return [];
    }
  }

  async getNetwork() {
    if (!this.provider) {
      return null;
    }

    try {
      const network = await this.provider.getNetwork();
      return {
        chainId: network.chainId,
        name: network.name
      };
    } catch (error) {
      console.error('Error getting network:', error);
      return null;
    }
  }

  isConnected() {
    return this.provider !== null && this.signer !== null && this.wallet !== null;
  }

  getErrorMessage() {
    return this.connectionError;
  }

  /**
   * Manually clear saved private key from browser storage
   * @returns {Promise<boolean>} True if key was cleared successfully
   */
  async clearSavedKey() {
    try {
      this.clearFromBrowser();
      console.log('🔑 Manually cleared saved private key from browser storage');
      
      // If currently connected with indefinite persistence, update flags
      if (this.indefinitePersistence) {
        this.indefinitePersistence = false;
        this.isPersistent = false;
      }
      
      return true;
    } catch (error) {
      console.error('Failed to clear saved private key:', error);
      return false;
    }
  }

  getEventConfig() {
    return {
      fallbackInterval: 15000, // Reduced frequency to respect Alchemy free tier limits
      connectionTimeout: 15000, // Shorter timeout - direct connection
      supportsRealTimeEvents: true
    };
  }

  // Private key specific methods

  /**
   * Set the target network for this provider
   * @param {number} chainId Chain ID to connect to
   */
  setTargetNetwork(chainId) {
    if (this.RPC_URLS[chainId]) {
      this.targetChainId = chainId;
      console.log(`🔑 Target network set to chain ID: ${chainId}`);
    } else {
      throw new Error(`Unsupported chain ID: ${chainId}`);
    }
  }

  /**
   * Get supported networks
   * @returns {Object} Supported networks configuration
   */
  getSupportedNetworks() {
    return Object.keys(this.RPC_URLS).map(chainId => ({
      chainId: parseInt(chainId),
      name: this.getNetworkName(parseInt(chainId)),
      rpcUrl: this.RPC_URLS[chainId]
    }));
  }

  /**
   * Generate a new random wallet
   * @returns {Object} New wallet info {privateKey, address, mnemonic}
   */
  generateNewWallet() {
    const wallet = ethers.Wallet.createRandom();
    return {
      privateKey: wallet.privateKey,
      address: wallet.address,
      mnemonic: wallet.mnemonic?.phrase || null
    };
  }

  /**
   * Import wallet from mnemonic phrase
   * @param {string} mnemonic Mnemonic phrase
   * @param {string} path Derivation path (optional)
   * @returns {string} Private key
   */
  importFromMnemonic(mnemonic, path = "m/44'/60'/0'/0/0") {
    try {
      const wallet = ethers.Wallet.fromMnemonic(mnemonic, path);
      return wallet.privateKey;
    } catch (error) {
      throw new Error(`Invalid mnemonic: ${error.message}`);
    }
  }

  /**
   * Get wallet balance
   * @returns {Promise<string>} Balance in POL
   */
  async getBalance() {
    if (!this.wallet || !this.provider) {
      return '0';
    }

    try {
      const balance = await this.provider.getBalance(this.wallet.address);
      return ethers.utils.formatEther(balance);
    } catch (error) {
      console.error('Error getting balance:', error);
      return '0';
    }
  }

  /**
   * Set session timeout duration
   * @param {number} minutes Timeout in minutes
   */
  setSessionTimeout(minutes) {
    this.sessionTimeoutMinutes = minutes;
    if (this.isConnected()) {
      this.setupSessionTimeout();
    }
  }

  /**
   * Check if session is still valid
   * @returns {boolean} True if session is valid
   */
  isSessionValid() {
    if (!this.connectionTime || !this.isConnected()) {
      return false;
    }
    
    const timeoutMs = this.sessionTimeoutMinutes * 60 * 1000;
    return (Date.now() - this.connectionTime) < timeoutMs;
  }

  // Private methods


  isValidPrivateKey(privateKey) {
    try {
      // Remove 0x prefix if present
      const cleanKey = privateKey.startsWith('0x') ? privateKey.slice(2) : privateKey;
      
      // Check length (64 hex characters)
      if (cleanKey.length !== 64) {
        return false;
      }
      
      // Check if it's valid hex
      if (!/^[0-9a-fA-F]+$/.test(cleanKey)) {
        return false;
      }
      
      // Try to create a wallet to validate
      new ethers.Wallet(privateKey);
      return true;
    } catch (error) {
      return false;
    }
  }

  isValidRpcUrl(rpcUrl) {
    try {
      const url = new URL(rpcUrl);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch (error) {
      return false;
    }
  }

  async initializeWithRpcUrl(rpcUrl) {
    try {
      this.provider = new ethers.providers.JsonRpcProvider(rpcUrl);
      console.log('🔑 Custom RPC wallet initialized with URL:', rpcUrl);

      // Test the connection
      await this.provider.getBlockNumber();
      console.log('✅ RPC connection verified');

    } catch (error) {
      this.connectionError = `Failed to connect to RPC: ${error.message}`;
      throw new Error(this.connectionError);
    }
  }

  getNetworkName(chainId) {
    const names = {
      31337: 'Local Hardhat',
      31338: 'DevCLI',
      80002: 'Polygon Amoy Testnet',
      137: 'Polygon Mainnet'
    };
    return names[chainId] || `Chain ${chainId}`;
  }

  setupSessionTimeout() {
    // Clear existing timeout
    if (this.sessionTimeout) {
      clearTimeout(this.sessionTimeout);
    }
    
    // Set new timeout
    const timeoutMs = this.sessionTimeoutMinutes * 60 * 1000;
    this.sessionTimeout = setTimeout(() => {
      console.log('🔑 Private key session expired, auto-disconnecting');
      this.disconnect();
      
      // Emit disconnect event if there are listeners
      this.emit?.('sessionExpired', { 
        message: 'Session expired for security. Please reconnect.' 
      });
    }, timeoutMs);
    
    console.log(`🔑 Session timeout set for ${this.sessionTimeoutMinutes} minutes`);
  }

  // Event methods (private key provider doesn't have native events, but we can simulate them)
  onAccountsChanged(callback) {
    // Private key provider doesn't change accounts automatically
    // This is here for interface compatibility
  }

  onChainChanged(callback) {
    // Private key provider connects to a specific chain
    // This is here for interface compatibility
  }

  onConnect(callback) {
    // Store callback for manual triggering
    this.connectCallback = callback;
  }

  onDisconnect(callback) {
    // Store callback for manual triggering
    this.disconnectCallback = callback;
  }

  emit(event, data) {
    // Simple event emission for interface compatibility
    if (event === 'connect' && this.connectCallback) {
      this.connectCallback(data);
    } else if (event === 'disconnect' && this.disconnectCallback) {
      this.disconnectCallback(data);
    }
  }

  // Persistence methods for localStorage support

  /**
   * Save private key to browser localStorage (with simple obfuscation)
   * @param {string} privateKey The private key to save
   * @param {string} persistenceMode The persistence mode ('timed' or 'indefinite')
   */
  saveToBrowser(privateKey, rpcUrl, persistenceMode = 'timed') {
    try {
      const address = new ethers.Wallet(privateKey).address;
      const obfuscatedKey = this.obfuscateKey(privateKey);

      const savedData = {
        obfuscatedKey: obfuscatedKey,
        rpcUrl: rpcUrl,
        timestamp: Date.now(),
        address: address, // for verification
        persistenceMode: persistenceMode,
        expiresAt: persistenceMode === 'timed' ? Date.now() + (8 * 60 * 60 * 1000) : null // 8 hours for timed
      };

      localStorage.setItem('gamemaker_custom_rpc_wallet_stored', JSON.stringify(savedData));
      console.log('🔑 Private key and RPC URL saved to localStorage');
    } catch (error) {
      console.error('Failed to save private key and RPC URL to localStorage:', error);
    }
  }

  /**
   * Load private key from browser localStorage
   * @returns {Object|null} Object with privateKey and persistenceMode, or null if not found/invalid/expired
   */
  loadFromBrowser() {
    try {
      const stored = localStorage.getItem('gamemaker_custom_rpc_wallet_stored');
      if (!stored) {
        return null;
      }

      const savedData = JSON.parse(stored);

      // Check expiration for timed persistence
      if (savedData.persistenceMode === 'timed' && savedData.expiresAt && Date.now() > savedData.expiresAt) {
        console.log('🔑 Saved custom RPC wallet expired, clearing storage');
        this.clearFromBrowser();
        return null;
      }

      const privateKey = this.deobfuscateKey(savedData.obfuscatedKey);

      // Verify the key is still valid
      const wallet = new ethers.Wallet(privateKey);
      if (wallet.address !== savedData.address) {
        console.warn('Saved private key address mismatch, clearing storage');
        this.clearFromBrowser();
        return null;
      }

      // Verify RPC URL is still valid
      if (!savedData.rpcUrl || !this.isValidRpcUrl(savedData.rpcUrl)) {
        console.warn('Saved RPC URL is invalid, clearing storage');
        this.clearFromBrowser();
        return null;
      }

      console.log(`🔑 Custom RPC wallet loaded from localStorage (${savedData.persistenceMode || 'timed'} mode)`);
      return {
        privateKey: privateKey,
        rpcUrl: savedData.rpcUrl,
        persistenceMode: savedData.persistenceMode || 'timed', // Default for backwards compatibility
        timestamp: savedData.timestamp,
        expiresAt: savedData.expiresAt
      };
    } catch (error) {
      console.error('Failed to load private key from localStorage:', error);
      this.clearFromBrowser(); // Clean up corrupted data
      return null;
    }
  }

  /**
   * Clear saved custom RPC wallet from browser localStorage
   */
  clearFromBrowser() {
    try {
      localStorage.removeItem('gamemaker_custom_rpc_wallet_stored');
      console.log('🔑 Cleared custom RPC wallet from localStorage');
    } catch (error) {
      console.error('Failed to clear custom RPC wallet from localStorage:', error);
    }
  }

  /**
   * Check if there's a saved custom RPC wallet in browser localStorage
   * @returns {boolean} True if a saved wallet exists
   */
  hasSavedKey() {
    try {
      const stored = localStorage.getItem('gamemaker_custom_rpc_wallet_stored');
      return stored !== null;
    } catch (error) {
      return false;
    }
  }

  /**
   * Auto-connect using saved custom RPC wallet from localStorage
   * @returns {Promise<boolean>} True if successfully connected
   */
  async autoConnectFromSaved() {
    const savedData = this.loadFromBrowser();
    if (!savedData) {
      return false;
    }

    try {
      await this.connect(savedData.privateKey, savedData.rpcUrl, savedData.persistenceMode);
      console.log(`🔑 Auto-connected using saved custom RPC wallet (${savedData.persistenceMode} mode)`);
      return true;
    } catch (error) {
      console.error('Auto-connect failed with saved custom RPC wallet:', error);
      this.clearFromBrowser(); // Clean up invalid data
      return false;
    }
  }

  /**
   * Get current persistence status
   * @returns {boolean} True if currently using persistent storage
   */
  isPersistenceEnabled() {
    return this.isPersistent;
  }

  /**
   * Get detailed persistence information
   * @returns {Object} Persistence mode details
   */
  getPersistenceInfo() {
    return {
      isPersistent: this.isPersistent,
      isIndefinite: this.indefinitePersistence,
      mode: this.indefinitePersistence ? 'indefinite' : (this.isPersistent ? 'timed' : 'session'),
      sessionTimeoutMinutes: this.sessionTimeoutMinutes,
      connectionTime: this.connectionTime
    };
  }

  // Simple obfuscation methods (NOT cryptographically secure!)

  /**
   * Apply simple obfuscation to private key (NOT encryption!)
   * @param {string} privateKey The private key to obfuscate
   * @returns {string} Obfuscated key
   */
  obfuscateKey(privateKey) {
    // Simple XOR with browser fingerprint (NOT secure, just obfuscation)
    const fingerprint = this.getBrowserFingerprint();
    const cleanKey = privateKey.startsWith('0x') ? privateKey.slice(2) : privateKey;
    
    let obfuscated = '';
    for (let i = 0; i < cleanKey.length; i++) {
      const keyChar = cleanKey.charCodeAt(i);
      const fingerprintChar = fingerprint.charCodeAt(i % fingerprint.length);
      obfuscated += String.fromCharCode(keyChar ^ fingerprintChar);
    }
    
    return btoa(obfuscated); // Base64 encode
  }

  /**
   * Remove obfuscation from private key
   * @param {string} obfuscatedKey The obfuscated key
   * @returns {string} Original private key
   */
  deobfuscateKey(obfuscatedKey) {
    try {
      const fingerprint = this.getBrowserFingerprint();
      const decoded = atob(obfuscatedKey);
      
      let deobfuscated = '';
      for (let i = 0; i < decoded.length; i++) {
        const obfuscatedChar = decoded.charCodeAt(i);
        const fingerprintChar = fingerprint.charCodeAt(i % fingerprint.length);
        deobfuscated += String.fromCharCode(obfuscatedChar ^ fingerprintChar);
      }
      
      return '0x' + deobfuscated;
    } catch (error) {
      throw new Error('Failed to deobfuscate private key');
    }
  }

  /**
   * Generate a simple browser fingerprint for obfuscation
   * @returns {string} Browser fingerprint
   */
  getBrowserFingerprint() {
    // Simple fingerprint - NOT for security, just obfuscation
    const ua = navigator.userAgent || '';
    const lang = navigator.language || '';
    const platform = navigator.platform || '';
    const screenInfo = `${window.screen?.width || 0}x${window.screen?.height || 0}`;
    
    return btoa(ua + lang + platform + screenInfo).slice(0, 32);
  }
}

export default PrivateKeyProvider;